/**
 * @file: lead.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Frontend client for the euler backend `LeadController`. One method per
 * endpoint; the backend injects tenancy + operator from the authenticated
 * identity, so requests carry neither.
 */

import { HttpClient } from '@euler/lib/request';
import { Lead, LeadIntention } from '@euler/model/lead/Lead';
import { LeadActivityView } from '@euler/model/lead/LeadActivity';
import {
  AddLeadCatalogItemsRequest,
  AddLeadItemsRequest,
  ConvertLeadRequest,
  CreateLeadRequest,
  CreateLeadResult,
  CreateManualLeadRequest,
  LeadBundleCandidates,
  LeadCoverageInfo,
  LeadDetail,
  LeadListFilter,
  LeadMessageView,
  LeadSorter,
  LeadStats,
  LogLeadActivityRequest,
  SendLeadMessageRequest,
  SetLeadItemDecisionRequest,
  TransitionLeadRequest,
  UpdateLeadActivityRequest,
  UpdateLeadRequest,
} from '@euler/model/lead/types';

export class LeadService {
  constructor(private readonly api: HttpClient) {}

  async list(
    filter: LeadListFilter | undefined,
    offset: number,
    limit: number,
    sorter?: LeadSorter,
  ): Promise<Lead[]> {
    return await this.api
      .get()
      .url('/leads', {
        filter: filter ? JSON.stringify(filter) : undefined,
        sorter: sorter ? JSON.stringify(sorter) : undefined,
        offset,
        limit,
      })
      .future();
  }

  // Aggregate counts powering the summary strip + status tabs. The backend
  // honors the filter (incl. `filter.keyword`) EXCEPT its status field.
  async stats(filter: LeadListFilter | undefined): Promise<LeadStats> {
    return await this.api
      .get()
      .url('/leads/stats', {
        filter: filter ? JSON.stringify(filter) : undefined,
      })
      .future();
  }

  async getDetail(leadId: string): Promise<LeadDetail> {
    return await this.api.get().url('/leads/:id', { id: leadId }).future();
  }

  // Feed affordance: coverage of a plan detail + the lead that owns it.
  async coverageByPlanDetail(planDetailId: number): Promise<LeadCoverageInfo> {
    return await this.api
      .get()
      .url('/leads/by-plan-detail/:planDetailId', { planDetailId })
      .future();
  }

  // The acted-on detail + bundleable same-vehicle uncovered details within an
  // adjustable window (days before/after the target's next service date).
  async getBundleCandidates(
    planDetailId: number,
    before?: number,
    after?: number,
  ): Promise<LeadBundleCandidates> {
    return await this.api
      .get()
      .url('/leads/bundle-candidates/:planDetailId', {
        planDetailId,
        before,
        after,
      })
      .future();
  }

  // Vehicle-anchored candidates for adding plan items to an existing lead: the
  // lead's other uncovered details within a window around its target date.
  async getBundleCandidatesForLead(
    leadId: string,
    before?: number,
    after?: number,
  ): Promise<LeadBundleCandidates> {
    return await this.api
      .get()
      .url('/leads/:id/bundle-candidates', { id: leadId, before, after })
      .future();
  }

  async create(request: CreateLeadRequest): Promise<CreateLeadResult> {
    return await this.api.post().url('/leads').data(request).future();
  }

  async createManualLead(
    request: CreateManualLeadRequest,
  ): Promise<CreateLeadResult> {
    return await this.api.post().url('/leads/manual').data(request).future();
  }

  async update(leadId: string, request: UpdateLeadRequest): Promise<void> {
    await this.api
      .put()
      .url('/leads/:id', { id: leadId })
      .data(request)
      .future();
  }

  async transition(
    leadId: string,
    request: TransitionLeadRequest,
  ): Promise<void> {
    await this.api
      .post()
      .url('/leads/:id/transition', { id: leadId })
      .data(request)
      .future();
  }

  async convert(leadId: string, request: ConvertLeadRequest): Promise<void> {
    await this.api
      .post()
      .url('/leads/:id/convert', { id: leadId })
      .data(request)
      .future();
  }

  // Manually set the lead's visit intention (到店意向) (marks it overridden so the
  // auto-derivation from outreach outcomes stops touching it).
  async setIntention(leadId: string, intention: LeadIntention): Promise<void> {
    await this.api
      .post()
      .url('/leads/:id/intention', { id: leadId })
      .data({ intention })
      .future();
  }

  async addItems(leadId: string, request: AddLeadItemsRequest): Promise<void> {
    await this.api
      .post()
      .url('/leads/:id/items', { id: leadId })
      .data(request)
      .future();
  }

  // Free "item-library" covered items picked from the service-subject catalog.
  async addCatalogItems(
    leadId: string,
    request: AddLeadCatalogItemsRequest,
  ): Promise<void> {
    await this.api
      .post()
      .url('/leads/:id/catalog-items', { id: leadId })
      .data(request)
      .future();
  }

  async setItemDecision(
    leadId: string,
    leadItemId: string,
    request: SetLeadItemDecisionRequest,
  ): Promise<void> {
    await this.api
      .put()
      .url('/leads/:id/items/:itemId', { id: leadId, itemId: leadItemId })
      .data(request)
      .future();
  }

  async removeItem(leadId: string, leadItemId: string): Promise<void> {
    await this.api
      .delete()
      .url('/leads/:id/items/:itemId', { id: leadId, itemId: leadItemId })
      .future();
  }

  async logActivity(
    leadId: string,
    request: LogLeadActivityRequest,
  ): Promise<void> {
    await this.api
      .post()
      .url('/leads/:id/activities', { id: leadId })
      .data(request)
      .future();
  }

  async listActivities(leadId: string): Promise<LeadActivityView[]> {
    return await this.api
      .get()
      .url('/leads/:id/activities', { id: leadId })
      .future();
  }

  async updateActivity(
    leadId: string,
    activityId: string,
    request: UpdateLeadActivityRequest,
  ): Promise<LeadActivityView> {
    return await this.api
      .put()
      .url('/leads/:id/activities/:activityId', { id: leadId, activityId })
      .data(request)
      .future();
  }

  // ----------------------------------------------- discussion (comments)

  async sendMessage(
    leadId: string,
    request: SendLeadMessageRequest,
  ): Promise<LeadMessageView> {
    return await this.api
      .post()
      .url('/leads/:id/messages', { id: leadId })
      .data(request)
      .future();
  }

  async listMessages(
    leadId: string,
    offset: number,
    limit: number,
  ): Promise<LeadMessageView[]> {
    return await this.api
      .get()
      .url('/leads/:id/messages', { id: leadId, offset, limit })
      .future();
  }

  async deleteMessage(leadId: string, messageId: string): Promise<void> {
    await this.api
      .delete()
      .url('/leads/:id/messages/:messageId', { id: leadId, messageId })
      .future();
  }
}
